///
/// EarthBrowser
///
/// City Labels Overlay - 2D Label Rendering
///
/// Manages a pool of 2D Label instances for city name display
/// Labels are positioned based on world-to-screen projection
/// and styled based on city importance tier
///
/// Features smooth fade transitions and hysteresis to prevent popping
///
/// Created by Matt Giger on December 28, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { labelOccupancy, type LabelRect } from '@/earthbrowser/design-system/LabelOccupancy'
import type { EarthBrowserContext } from '@/earthbrowser/EarthBrowserContext'
import type { RenderActionSet } from '@stovokor/engine/Renderer'
import type { RenderState } from '@stovokor/engine/RenderState'
import { GestureState, type GestureHitResult } from '@stovokor/gestures/GestureRecognizer'
import { type HoverGestureEventData, HoverGestureRecognizer } from '@stovokor/gestures/HoverGestureRecognizer'
import { type TapGestureEventData, TapGestureRecognizer } from '@stovokor/gestures/TapGestureRecognizer'
import { rgba, white, yellow, type color } from '@stovokor/math/color'
import { type StovokorContext } from '@stovokor/StovokorContext'
import { Canvas } from '@stovokor/uipanes/Canvas'
import { Font, FontFamily } from '@stovokor/uipanes/Font'
import { Pane, bind, DepthPaneRenderer } from '@stovokor/uipanes/Pane'
import type { CityImportance } from './city-types'
import type { LocationsDatasetLayer } from './LocationsDatasetLayer'
import { MeasurementType, shouldUseMetric } from '@/earthbrowser/utils/UnitSystem'
import {
	drawAuroraArc,
	drawTrendChevron,
	drawWeatherIcon,
	drawWindArrow,
	getTempColor,
	getTempTextColor,
	isFreezing,
	isSignificantWeather,
	resolveIconCode,
	type WeatherIconCode
} from './CityWeatherStyle'

/**
 * Weather info for city label display
 */
export interface CityWeatherInfo {
	temp_c: number | null
	weather_code: string | null
	weather_desc: string | null
	cloud_cover_pct: number | null
	feels_like_c: number | null
	wind_speed_mps: number | null
	wind_dir_deg: number | null
	pressure_trend_3h_hpa: number | null
	/** City is on the night side of the terminator (moon icon variants) */
	is_night: boolean
	/** Aurora probability (%) when meaningful for this city tonight, else null */
	aurora_chance_pct: number | null
	/** Camera is low enough that wind arrows are useful (set by the layer) */
	show_wind: boolean
}

/**
 * Per-tier weather detail: progressive disclosure so capitals get the full
 * treatment while towns stay as plain cartographic labels. The name is
 * always line 1 on its own (the cartographic label); weather is one glyph
 * on line 2 - condition icon fused to a temperature pill - so it reads as
 * a single mark rather than an icon on one line and a number on another.
 */
interface TierWeatherStyle {
	icon: 'always' | 'significant' | 'never'  // condition icon left of the temperature
	chip: boolean       // temperature pill
	tempText: boolean   // bare colored temp text (small tier, no pill)
	extras: boolean     // wind arrow, pressure trend, feels-like text
}

const TIER_WEATHER_STYLE: Record<CityImportance, TierWeatherStyle> = {
	capital: { icon: 'always', chip: true, tempText: false, extras: true },
	major: { icon: 'always', chip: true, tempText: false, extras: true },
	medium: { icon: 'always', chip: true, tempText: false, extras: false },
	// Small towns show the icon only when something is falling (rain bands
	// stay visible at town granularity without a sun on every label)
	small: { icon: 'significant', chip: false, tempText: true, extras: false },
	town: { icon: 'never', chip: false, tempText: false, extras: false }
}

/** Feels-like text appears only when it diverges this much from air temp */
const FEELS_LIKE_DIVERGENCE_C = 4
/** Pressure trend chevron appears beyond this 3h change */
const PRESSURE_TREND_THRESHOLD_HPA = 1
/** Wind arrow appears above this speed */
const WIND_ARROW_MIN_MPS = 1.5

/**
 * Temperature redundancy filter for the small tier. A metro at close zoom
 * puts a dozen towns on screen all reading within a degree of the big
 * city's pill; repeating the number adds ink, not information. A small
 * label drops its bare temperature when a pill-bearing label within this
 * radius shows (in display units, rounded) a value this close - so the
 * towns that remain numbered are the ones that differ: the cold valley,
 * the warm coast. Hover still gives every town its own reading.
 */
const TEMP_DEDUPE_RADIUS_PX = 240
const TEMP_DEDUPE_MIN_DIFF_DEG = 2
const TEMP_DEDUPE_INTERVAL_MS = 300

/** Key under which on-screen city label rects are shared with other label systems */
const OCCUPANCY_OWNER = 'city-labels'

/** Temperature in display units, rounded as the label shows it */
function displayTemp(tempC: number): number {
	return Math.round(shouldUseMetric(MeasurementType.TEMPERATURE) ? tempC : tempC * 9 / 5 + 32)
}

/**
 * City label data for 2D rendering
 */
export interface CityLabelData {
	id: string
	name: string
	importance: CityImportance
	screenX: number           // Label center X (may be offset from city)
	screenY: number           // Label center Y (may be offset from city)
	cityScreenX: number       // Actual city location X
	cityScreenY: number       // Actual city location Y
	// Target opacity (altitude and grazing-angle fades). With shouldShow
	// false, 0 marks a geometric hide (city edge-on or behind the limb):
	// the viewer can see why the label is going, so it dissolves without
	// the hide hysteresis instead of lingering past the horizon
	alpha: number
	worldDepth: number        // Perspective NDC depth [0,1] for gl_FragDepth occlusion
	shouldShow: boolean  // true = visible, false = just updating position for fade out
	weather?: CityWeatherInfo  // Optional weather data
}

/**
 * Camera motion state, provided by LocationsDatasetLayer. Timing is
 * cause-aware: pan-time selection changes are treated as suspect (long
 * hysteresis - contention jitter must never reach the screen), while
 * zoom-time changes are legitimate and monotone (altitude gating and
 * collision are pure functions of zoom), so labels respond quickly -
 * evicted labels dissolve before they can pile up on zoom out, and
 * newly revealed labels appear promptly on zoom in.
 */
export type LabelMotion = 'idle' | 'panning' | 'zooming'

/**
 * Animation timing constants
 */
const FADE_IN_DURATION_MS = 400     // Time for fade in (smooth appearance)
const FADE_IN_ZOOM_MS = 250         // Faster reveal while zooming
const FADE_OUT_DURATION_MS = 250    // Time for fade out (dissolve, don't pop)
const FADE_OUT_ZOOM_MS = 150        // Faster dissolve while zooming

// Pan/idle hysteresis must span multiple placement passes
// (PLACEMENT_INTERVAL_MS = 150 in LocationsDatasetLayer): a label has to
// stay selected for several consecutive passes before it starts appearing,
// and stay deselected even longer before it starts fading, so marginal
// cities that flip between passes never reach the screen.
const HYSTERESIS_SHOW_MS = 500       // Delay before showing a new label (~3 passes)
const HYSTERESIS_HIDE_MS = 1000      // Delay before hiding (~6 passes)
const HYSTERESIS_SHOW_PAN_MS = 700   // Show delay while camera is panning
const HYSTERESIS_HIDE_PAN_MS = 1500  // Hide delay while camera is panning
const HYSTERESIS_SHOW_ZOOM_MS = 150  // Zoom reveals labels promptly (~1 pass)
const HYSTERESIS_HIDE_ZOOM_MS = 100  // Zoom evictions start dissolving immediately

/**
 * Font configurations for each importance tier
 * Larger fonts for more important cities
 */
const CITY_LABEL_FONTS: Record<CityImportance, Font> = {
	capital: new Font(FontFamily.sansserif, 17, 'bold'),
	major: new Font(FontFamily.sansserif, 16, 'bold'),
	medium: new Font(FontFamily.sansserif, 14, 'normal'),
	small: new Font(FontFamily.sansserif, 13, 'normal'),
	town: new Font(FontFamily.sansserif, 12, 'normal')
}

/**
 * Text colors for each importance tier
 * Bright white for all tiers
 */
const CITY_LABEL_COLORS: Record<CityImportance, color> = {
	capital: white,
	major: white,
	medium: white,
	small: rgba(1, 1, 1, 0.95),
	town: rgba(1, 1, 1, 0.9)
}

/**
 * A measured element of the weather row (icon, pill, text, glyphs)
 */
interface WeatherRowSegment {
	kind: 'icon' | 'chip' | 'tempText' | 'feels' | 'wind' | 'trend' | 'aurora'
	width: number
	/** Gap to the next segment (the icon hugs the temperature) */
	gapAfter: number
	text?: string
	tempC?: number
	iconCode?: WeatherIconCode
}

const ROW_SEGMENT_GAP = 5
const ICON_TEMP_GAP = 2

/**
 * Custom canvas for city labels with crisp outline effect and optional weather
 */
class CityLabelCanvas extends Canvas {
	private _text?: string
	private _font: Font
	private _color: color
	private _offsetX: number = 0
	private _offsetY: number = 0
	private _weather?: CityWeatherInfo
	private _tempFont: Font
	private _importance: CityImportance = 'town'
	// Bare temperature hidden by the overlay's redundancy filter (small tier)
	private _tempSuppressed = false
	// City dot position relative to label top-left
	private _cityDotX: number = 0
	private _cityDotY: number = 0

	constructor(text: string | undefined, font: Font, textColor: color, importance: CityImportance) {
		super()
		this._text = text
		this._font = font
		this._color = textColor
		this._importance = importance
		this._tempFont = new Font(font.family, Math.max(9, font.size - 4), 'bold')

		// Set initial size with extra padding for outline
		this.rerender(100, font.size + 9, 1.0)
		this._needsUpdate = true
	}

	public internalOffset(x: number, y: number): void {
		this._offsetX = x
		this._offsetY = y
		this._needsUpdate = true
	}

	/**
	 * Set the city dot position relative to the label's top-left corner
	 */
	public setCityDotOffset(relX: number, relY: number): void {
		if (this._cityDotX !== relX || this._cityDotY !== relY) {
			this._cityDotX = relX
			this._cityDotY = relY
			this._needsUpdate = true
		}
	}

	public get text(): string | undefined { return this._text }
	public set text(value: string | undefined) {
		if (value !== this._text) {
			this._text = value
			this._needsUpdate = true
		}
	}

	public get font(): Font { return this._font }
	public set font(value: Font) {
		if (value !== this._font) {
			this._font = value
			this._tempFont = new Font(value.family, Math.max(9, value.size - 4), 'bold')
			this._needsUpdate = true
		}
	}

	public get importance(): CityImportance { return this._importance }
	public set importance(value: CityImportance) {
		if (value !== this._importance) {
			this._importance = value
			this._needsUpdate = true
		}
	}

	public get color(): color { return this._color }
	public set color(value: color) {
		// Only trigger re-render if color components changed significantly
		// This prevents constant re-renders during smooth alpha transitions
		const alphaChanged = Math.abs(value.a - this._color.a) > 0.02
		const colorChanged = value.r !== this._color.r || value.g !== this._color.g || value.b !== this._color.b
		if (alphaChanged || colorChanged) {
			this._color = value
			this._needsUpdate = true
		}
	}

	public get weather(): CityWeatherInfo | undefined { return this._weather }
	public set weather(value: CityWeatherInfo | undefined) {
		// Check if any displayed field actually changed
		const a = this._weather
		const changed = a?.temp_c !== value?.temp_c ||
			a?.weather_code !== value?.weather_code ||
			a?.feels_like_c !== value?.feels_like_c ||
			a?.wind_speed_mps !== value?.wind_speed_mps ||
			a?.wind_dir_deg !== value?.wind_dir_deg ||
			a?.pressure_trend_3h_hpa !== value?.pressure_trend_3h_hpa ||
			a?.is_night !== value?.is_night ||
			a?.aurora_chance_pct !== value?.aurora_chance_pct ||
			a?.show_wind !== value?.show_wind
		if (changed) {
			this._weather = value
			this._needsUpdate = true
		}
	}

	public get tempSuppressed(): boolean { return this._tempSuppressed }
	public set tempSuppressed(value: boolean) {
		if (value !== this._tempSuppressed) {
			this._tempSuppressed = value
			this._needsUpdate = true
		}
	}

	/**
	 * Temperature in display units (°C or °F), degree-only (units live in
	 * the hover panel - broadcast maps never repeat the unit on every label)
	 */
	private formatTempShort(tempC: number): string {
		return `${displayTemp(tempC)}°`
	}

	/**
	 * Wind speed in display units, number only
	 */
	private formatWindShort(speedMps: number): string {
		const display = shouldUseMetric(MeasurementType.SPEED)
			? speedMps * 3.6      // km/h
			: speedMps * 2.237    // mph
		return `${Math.round(display)}`
	}

	/** Pill height: tight around the digits so the name stays the heavier line */
	private get tempChipHeight(): number {
		return this._tempFont.size + 5
	}

	/** Condition icon box: pill-height for pill tiers, smaller for bare-text towns */
	private iconSize(style: TierWeatherStyle): number {
		return style.chip ? this.tempChipHeight + 3 : this._tempFont.size + 3
	}

	/**
	 * Measure the weather row segments for the current tier and weather.
	 * Shared by intrinsicSize() and render() so layout and drawing agree.
	 */
	private computeWeatherRow(): WeatherRowSegment[] {
		const w = this._weather
		if (w?.temp_c == null) return []

		const style = TIER_WEATHER_STYLE[this._importance]
		const context = this.context
		const segments: WeatherRowSegment[] = []
		context.font = this._tempFont.toString()

		// Condition icon, fused to the temperature that follows it
		const iconCode = resolveIconCode(w.weather_code, w.temp_c)
		const showIcon = iconCode !== 'unknown' && (
			style.icon === 'always' || (style.icon === 'significant' && isSignificantWeather(iconCode))
		)
		if (showIcon) {
			segments.push({ kind: 'icon', width: this.iconSize(style), gapAfter: ICON_TEMP_GAP, iconCode })
		}

		if (style.chip) {
			const text = this.formatTempShort(w.temp_c)
			// Side padding scales with the pill so the ends stay fully round
			segments.push({ kind: 'chip', width: context.measureText(text).width + this.tempChipHeight * 0.9, gapAfter: ROW_SEGMENT_GAP, text, tempC: w.temp_c })
		} else if (style.tempText && !this._tempSuppressed) {
			const text = this.formatTempShort(w.temp_c)
			segments.push({ kind: 'tempText', width: context.measureText(text).width, gapAfter: ROW_SEGMENT_GAP, text, tempC: w.temp_c })
		} else if (!showIcon) {
			return []
		}

		if (style.extras) {
			// One colour-coded value per label: feels-like is plain text, not
			// a second pill competing with the air temperature for attention
			if (w.feels_like_c != null && Math.abs(w.feels_like_c - w.temp_c) >= FEELS_LIKE_DIVERGENCE_C) {
				const text = `feels ${this.formatTempShort(w.feels_like_c)}`
				segments.push({ kind: 'feels', width: context.measureText(text).width, gapAfter: ROW_SEGMENT_GAP, text, tempC: w.feels_like_c })
			}

			if (w.show_wind && w.wind_speed_mps != null && w.wind_dir_deg != null && w.wind_speed_mps >= WIND_ARROW_MIN_MPS) {
				const text = this.formatWindShort(w.wind_speed_mps)
				segments.push({ kind: 'wind', width: this._tempFont.size + 2 + context.measureText(text).width, gapAfter: ROW_SEGMENT_GAP, text })
			}

			if (w.pressure_trend_3h_hpa != null && Math.abs(w.pressure_trend_3h_hpa) >= PRESSURE_TREND_THRESHOLD_HPA) {
				segments.push({ kind: 'trend', width: this._tempFont.size * 0.9, gapAfter: ROW_SEGMENT_GAP })
			}
		}

		if (style.chip && w.is_night && w.aurora_chance_pct != null) {
			segments.push({ kind: 'aurora', width: this._tempFont.size, gapAfter: ROW_SEGMENT_GAP })
		}

		// The icon alone (small town in rain, temperature deduplicated) is
		// still a valid row
		return segments
	}

	private rowWidth(segments: WeatherRowSegment[]): number {
		let total = 0
		segments.forEach((s, i) => {
			total += s.width + (i < segments.length - 1 ? s.gapAfter : 0)
		})
		return total
	}

	/**
	 * Always render at exactly the intrinsic content size.
	 *
	 * The base Canvas only grows toward the intrinsic size, so a label
	 * reused from the pool could keep a stale larger canvas, and any
	 * mismatch between canvas size and content persists in the uploaded
	 * texture until the content changes. Rendering at the exact intrinsic
	 * size (and reporting shrinks as well as grows) keeps the pane frame,
	 * canvas, and texture in lockstep every repaint.
	 */
	public override rerender(width: number, height: number, scale: number): boolean {
		// No measuring context yet (first call from the constructor)
		if (!this._context) {
			return super.rerender(width, height, scale)
		}

		const [iw, ih] = this.intrinsicSize()
		if (iw <= 0 || ih <= 0) {
			return super.rerender(width, height, scale)
		}

		const changed = iw !== this.width || ih !== this.height
		const grown = super.rerender(iw, ih, scale)
		return changed || grown
	}

	/** Height of the weather row: the pill, or the icon when it is the taller */
	private rowHeight(row: WeatherRowSegment[]): number {
		const icon = row.find(s => s.kind === 'icon')
		return Math.max(this.tempChipHeight, icon?.width ?? 0)
	}

	override intrinsicSize(): [number, number] {
		if (this._text !== undefined) {
			const context = this.context
			context.font = this._font.toString()
			const nameMetrics = context.measureText(this._text)
			const nameWidth = nameMetrics.width

			const row = this.computeWeatherRow()
			const rowW = this.rowWidth(row)

			const width = Math.ceil(Math.max(nameWidth, rowW)) + 12

			const lineHeight = Math.ceil(nameMetrics.fontBoundingBoxAscent + nameMetrics.fontBoundingBoxDescent)
			const height = row.length > 0
				? lineHeight + this.rowHeight(row) + 10
				: lineHeight + 8

			return [width, height]
		}
		return [this.width, this.height]
	}

	override render(): void {
		if (this._text === undefined) return

		const context = this.context
		const weather = this._weather

		// Center each line explicitly from its measured width rather than
		// relying on textAlign "center". Anchor-based centering depends on
		// the engine's shaped-run width at draw time, which can transiently
		// disagree with layout (e.g. during font loading) and leave a line
		// anchored at the midpoint and clipped at the canvas edge.
		context.textAlign = "left"
		context.textBaseline = "top"

		// Center x position
		const centerX = this._offsetX + this.width / 2
		const y = this._offsetY + 4

		// Apply alpha to both stroke and fill
		const alpha = this._color.a

		// ===== Line 1: city name (the cartographic label, nothing else) =====
		context.font = this._font.toString()
		const nameMetrics = context.measureText(this._text)
		const lineHeight = nameMetrics.fontBoundingBoxAscent + nameMetrics.fontBoundingBoxDescent
		const nameX = centerX - nameMetrics.width / 2

		// Draw dark stroke outline first
		context.strokeStyle = `rgba(0, 0, 0, ${0.85 * alpha})`
		context.lineWidth = 3
		context.lineJoin = "round"
		context.miterLimit = 2
		context.strokeText(this._text, nameX, y)

		// Draw bright white fill on top
		context.fillStyle = this._color.toString()
		context.fillText(this._text, nameX, y)

		// ===== Line 2: condition icon + temperature pill + extras (tier-gated) =====
		const row = this.computeWeatherRow()
		if (row.length > 0 && weather) {
			const rowW = this.rowWidth(row)
			const chipH = this.tempChipHeight
			const rowH = this.rowHeight(row)
			const rowTop = y + lineHeight + 1
			const rowCenterY = rowTop + rowH / 2
			const chipY = rowCenterY - chipH / 2
			let x = centerX - rowW / 2

			context.font = this._tempFont.toString()

			// Digits don't fill the font's em box (there is unused descender
			// space below them), so centering the em box leaves the text
			// sitting high in the pill. Center the measured glyph ink instead.
			context.textBaseline = "alphabetic"
			const inkCenteredY = (text: string): number => {
				const metrics = context.measureText(text)
				const inkHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent
				return chipY + (chipH - inkHeight) / 2 + metrics.actualBoundingBoxAscent
			}
			const outlinedText = (text: string, tx: number, ty: number, fill: string) => {
				context.strokeStyle = `rgba(0, 0, 0, ${0.85 * alpha})`
				context.lineWidth = 2.5
				context.strokeText(text, tx, ty)
				context.fillStyle = fill
				context.fillText(text, tx, ty)
			}

			for (const segment of row) {
				switch (segment.kind) {
					case 'icon': {
						drawWeatherIcon(context, segment.iconCode ?? 'unknown', weather.is_night, x + segment.width / 2, rowCenterY, segment.width, alpha)
						break
					}
					case 'chip': {
						// Temperature pill: colored, contrast-aware text. A filled
						// shape keeps the temp readable over any terrain (tinted
						// text sinks into similarly-colored ground) and gives the
						// colour enough area to be judged; the fully round ends
						// keep it lighter than the name above. At or below
						// freezing the hairline turns icy - the one threshold
						// that changes decisions more than the number does
						const tempC = segment.tempC ?? 0
						context.beginPath()
						context.roundRect(x, chipY, segment.width, chipH, chipH / 2)
						context.fillStyle = getTempColor(tempC, alpha)
						context.fill()
						context.strokeStyle = isFreezing(tempC)
							? `rgba(200, 232, 255, ${0.95 * alpha})`
							: `rgba(0, 0, 0, ${0.45 * alpha})`
						context.lineWidth = 1
						context.stroke()

						const chipText = segment.text ?? ''
						const textW = context.measureText(chipText).width
						context.fillStyle = getTempTextColor(tempC, alpha)
						context.fillText(chipText, x + (segment.width - textW) / 2, inkCenteredY(chipText))
						break
					}
					case 'tempText': {
						// Small tier: bare colored temp with outline, no pill
						const bareText = segment.text ?? ''
						outlinedText(bareText, x, inkCenteredY(bareText), getTempColor(segment.tempC ?? 0, alpha))
						break
					}
					case 'feels': {
						const feelsText = segment.text ?? ''
						outlinedText(feelsText, x, inkCenteredY(feelsText), `rgba(255, 255, 255, ${0.85 * alpha})`)
						break
					}
					case 'wind': {
						const arrowSize = this._tempFont.size
						drawWindArrow(context, x + arrowSize / 2, rowCenterY, arrowSize, weather.wind_dir_deg ?? 0, alpha)
						const windText = segment.text ?? ''
						const textX = x + arrowSize + 2
						const textY = inkCenteredY(windText)
						context.strokeStyle = `rgba(0, 0, 0, ${0.85 * alpha})`
						context.lineWidth = 2.5
						context.strokeText(windText, textX, textY)
						context.fillStyle = `rgba(207, 230, 255, ${alpha})`
						context.fillText(windText, textX, textY)
						break
					}
					case 'trend': {
						const rising = (weather.pressure_trend_3h_hpa ?? 0) > 0
						drawTrendChevron(context, x + segment.width / 2, rowCenterY, this._tempFont.size, rising, alpha)
						break
					}
					case 'aurora': {
						drawAuroraArc(context, x + segment.width / 2, rowCenterY, this._tempFont.size, alpha)
						break
					}
				}
				x += segment.width + segment.gapAfter
			}

			context.textBaseline = "top"
		}

		// Draw subtle city location dot
		const dotX = this._cityDotX
		const dotY = this._cityDotY
		// Only draw if dot is within reasonable distance (visible on canvas)
		if (dotX >= -50 && dotX <= this.width + 50 && dotY >= -50 && dotY <= this.height + 50) {
			context.beginPath()
			context.arc(dotX, dotY, 3, 0, Math.PI * 2)
			context.fillStyle = `rgba(255, 255, 255, ${0.7 * alpha})`
			context.fill()
			context.strokeStyle = `rgba(0, 0, 0, ${0.5 * alpha})`
			context.lineWidth = 1
			context.stroke()
		}
	}
}

/**
 * Custom city label pane with crisp outline effect
 */
class CityLabel extends Pane {
	private cityLayer: LocationsDatasetLayer
	private textCanvas: CityLabelCanvas
	private depthRenderer: DepthPaneRenderer
	public cityId: string | null = null

	constructor(context: StovokorContext, cityLayer: LocationsDatasetLayer, text: string | undefined, fontStyle: Font, textColor: color, importance: CityImportance) {
		super(context)
		this.cityLayer = cityLayer

		this.depthRenderer = new DepthPaneRenderer(context, this)
		this.renderer = this.depthRenderer

		this.size(100, fontStyle.size + 8)
		this.adoptIntrinsicSize = true
		this.textCanvas = new CityLabelCanvas(text, fontStyle, textColor, importance)
		this._contents = this.textCanvas
		if (text) {
			this.textCanvas.text = text
		}

		// Ground-feature priorities: above Earth (tap 10) so city taps beat the
		// bare globe, but below dataset entities (tap 20, hover 5) so webcams,
		// buoys, etc. win when their markers overlap a city label
		const tapRecognizer = new TapGestureRecognizer()
		tapRecognizer.priority = 15
		tapRecognizer.onGesture(this.tapHandler.bind(this))
		this.addRecognizer(tapRecognizer)

		const hoverRecognizer = new HoverGestureRecognizer()
		hoverRecognizer.priority = 2
		hoverRecognizer.onGesture(this.hoverHandler.bind(this))
		this.addRecognizer(hoverRecognizer)
	}

	public set worldDepth(value: number) {
		this.depthRenderer.worldDepth.value = value
	}

	/**
	 * Report the label hit at the city's world distance (km) instead of the
	 * default 2D pane depth (~0). Pane hits at distance ~0 max out the gesture
	 * arena's distance boost (~500 points), which let city labels beat every
	 * 3D node regardless of base priority. Using the real camera-to-city
	 * distance puts labels on the same scale as world hits, so the base
	 * priorities above decide (datasets > cities > globe).
	 */
	public override gestureHitTest2D(x: number, y: number): GestureHitResult | undefined {
		const hit = super.gestureHitTest2D(x, y)
		if (hit && this.cityId) {
			const cityNode = this.cityLayer.getCityNodeById(this.cityId)
			const camera = this.context.focusManager.getViewportHandler()?.camera
			if (cityNode && camera) {
				hit.distance = camera.position.subtract(cityNode.cartesianPosition).length
			}
		}
		return hit
	}

	private tapHandler(_event: TapGestureEventData): void {
		if(this.cityId) {
			const cityNode = this.cityLayer.getCityNodeById(this.cityId)
			// Restricted contexts (embeds) limit which entities a tap may focus
			if(cityNode && (this.context as EarthBrowserContext).canFocusEntity(cityNode)) {
				cityNode.requestTapFocus()
			}
		}
	}

	private hoverHandler(event: HoverGestureEventData): void {
		const ebContext = this.context as EarthBrowserContext
		switch (event.state) {
			case GestureState.Began: {
				this.textCanvas.color = yellow
				// Show the glass hover callout with full conditions + nearby data
				if (this.cityId) {
					const cityNode = this.cityLayer.getCityNodeById(this.cityId)
					if (cityNode && ebContext.focusManager.getFocusedNode() !== cityNode) {
						ebContext.hoveredObjectCallout?.previewFor(cityNode)
					}
				}
				break
			}
			case GestureState.Ended: {
				this.textCanvas.color = white
				if (this.cityId) {
					const cityNode = this.cityLayer.getCityNodeById(this.cityId)
					if (cityNode) {
						ebContext.hoveredObjectCallout?.hidePreview(cityNode)
					}
				}
				break
			}
		}
	}

	public get text(): string | undefined { return this.textCanvas.text }
	public set text(value: string) { this.textCanvas.text = value }

	public get font(): Font { return this.textCanvas.font }
	public set font(value: Font) { this.textCanvas.font = value }

	public get importance(): CityImportance { return this.textCanvas.importance }
	public set importance(value: CityImportance) { this.textCanvas.importance = value }

	public get color(): color { return this.textCanvas.color }
	public set color(value: color) { this.textCanvas.color = value }

	public get weather(): CityWeatherInfo | undefined { return this.textCanvas.weather }
	public set weather(value: CityWeatherInfo | undefined) { this.textCanvas.weather = value }

	public get tempSuppressed(): boolean { return this.textCanvas.tempSuppressed }
	public set tempSuppressed(value: boolean) { this.textCanvas.tempSuppressed = value }

	public setCityDotOffset(relX: number, relY: number): void {
		this.textCanvas.setCityDotOffset(relX, relY)
	}

	/**
	 * Force the label canvas to re-measure and repaint on the next frame
	 */
	public repaint(): void {
		this.textCanvas.setNeedsUpdate()
	}
}

/**
 * Animation state for a label
 */
interface LabelAnimState {
	currentAlpha: number      // Current displayed alpha (0-1)
	targetAlpha: number       // Target alpha we're animating towards
	lastUpdateTime: number    // Last time this label's target was updated
	stateChangeTime: number   // When the show/hide state last changed
	importance: CityImportance
	screenX: number           // Label position
	screenY: number
	cityScreenX: number       // Actual city position
	cityScreenY: number
	worldDepth: number        // Perspective depth for gl_FragDepth occlusion
	weather?: CityWeatherInfo  // Current weather data for this city
}

/**
 * Pooled city label with its CityLabel instance and animation state
 */
interface PooledLabel {
	label: CityLabel
	cityId: string | null
	animState: LabelAnimState
}

/**
 * 2D overlay for city labels
 *
 * This Pane manages a pool of Label instances that are positioned
 * based on world-to-screen projection of city coordinates.
 * It receives a list of visible cities each frame from the
 * LocationsDatasetLayer and updates the labels accordingly.
 *
 * Features:
 * - Smooth fade in/out transitions (400ms in, 250ms out)
 * - Hysteresis to prevent label flickering at visibility boundaries
 */
export class CityLabelsOverlay extends Pane {
	private labelPool: PooledLabel[] = []
	private activeLabels = new Map<string, PooledLabel>()
	private lastFrameTime: number = 0
	private cityLayer: LocationsDatasetLayer

	// Reusable array for labels to remove (avoids allocation every frame)
	private labelsToRemove: string[] = []

	// Camera motion state - selects cause-aware hysteresis/fade timing
	private motion: LabelMotion = 'idle'

	// Last run of the small-tier temperature redundancy filter
	private lastDedupeTime = 0

	// Pool size limits per importance tier
	// High density coverage - must support 400+ visible labels
	private readonly poolSizes: Record<CityImportance, number> = {
		capital: 80,
		major: 150,
		medium: 200,
		small: 150,
		town: 120
	}

	constructor(context: EarthBrowserContext, cityLayer: LocationsDatasetLayer) {
		super(context)
		this.cityLayer = cityLayer

		// Fill the viewport
		this.bind(bind.width | bind.height)
		this.transparency = true

		// Initialize label pools for each importance tier
		this.initializeLabelPool()

		// Labels only repaint when their content changes, so text painted
		// with interim font state (fonts still loading) would otherwise stay
		// frozen in the cached textures. Repaint everything once fonts settle.
		if (typeof document !== 'undefined' && document.fonts) {
			document.fonts.ready.then(() => this.repaintAllLabels()).catch(() => {})
			document.fonts.addEventListener('loadingdone', this.fontsLoadedHandler)
		}
	}

	private fontsLoadedHandler = (): void => {
		this.repaintAllLabels()
	}

	/**
	 * Mark every pooled label canvas dirty so it re-measures and repaints
	 */
	private repaintAllLabels(): void {
		for (const pooled of this.labelPool) {
			pooled.label.repaint()
		}
	}

	/**
	 * Initialize the label pool with pre-created CityLabel instances
	 */
	private initializeLabelPool(): void {
		const importanceTiers: CityImportance[] = ['capital', 'major', 'medium', 'small', 'town']

		for (const importance of importanceTiers) {
			const count = this.poolSizes[importance]
			const font = CITY_LABEL_FONTS[importance]
			const labelColor = CITY_LABEL_COLORS[importance]

			for (let i = 0; i < count; i++) {
				const label = new CityLabel(this.context, this.cityLayer, '', font, labelColor, importance)
				label.visible = false

				// Set depth based on importance (capitals on top)
				const depthOffset = importanceTiers.indexOf(importance)
				label.depth = 10 - depthOffset

				this.addNode(label)

			this.labelPool.push({
				label,
				cityId: null,
				animState: {
					currentAlpha: 0,
					targetAlpha: 0,
					lastUpdateTime: 0,
					stateChangeTime: 0,
					importance,
					screenX: 0,
					screenY: 0,
					cityScreenX: 0,
					cityScreenY: 0,
					worldDepth: 1.0
				}
			})
			}
		}
	}

	/**
	 * Get a label from the pool for a specific importance tier
	 */
	private getPooledLabel(importance: CityImportance): PooledLabel | undefined {
		const font = CITY_LABEL_FONTS[importance]
		const labelColor = CITY_LABEL_COLORS[importance]

		// First, try to find an unused label with matching font
		for (const pooled of this.labelPool) {
			if (pooled.cityId === null && pooled.animState.currentAlpha === 0 && pooled.label.font === font) {
				return pooled
			}
		}

		// If no matching font found, find any unused label and reconfigure it
		for (const pooled of this.labelPool) {
			if (pooled.cityId === null && pooled.animState.currentAlpha === 0) {
				pooled.label.font = font
				pooled.label.color = labelColor
				pooled.label.importance = importance
				pooled.animState.importance = importance
				return pooled
			}
		}

		return undefined
	}

	/**
	 * Get the set of currently active city IDs (including those fading out)
	 * Used by LocationsDatasetLayer to provide position updates for fading labels
	 */
	public getActiveCityIds(): Set<string> {
		return new Set(this.activeLabels.keys())
	}

	/**
	 * Update labels with visible cities
	 *
	 * Called each frame by LocationsDatasetLayer with the list of
	 * cities that should be displayed and their screen positions.
	 * Includes cities that are fading out (shouldShow=false) for position updates.
	 *
	 * @param cities - Array of city label data to display
	 * @param motion - Camera motion state (selects hysteresis/fade timing)
	 */
	public updateLabels(cities: CityLabelData[], motion: LabelMotion = 'idle'): void {
		this.motion = motion
		const now = performance.now()

		// Build a map of all city data (including fading ones)
		const cityMap = new Map(cities.map(c => [c.id, c]))

		// Process labels that are no longer in any list - start fading out
		for (const [cityId, pooled] of this.activeLabels) {
			if (!cityMap.has(cityId)) {
				// No position update available - start fade out if not already fading
				if (pooled.animState.targetAlpha > 0) {
					pooled.animState.targetAlpha = 0
					pooled.animState.stateChangeTime = now
				}
			}
		}

		// console.log('updateLabels', cities.length)

		// Update or create labels for all cities in the list
		for (const city of cities) {
			let pooled = this.activeLabels.get(city.id)

			if (!pooled) {
				// Only create new labels for cities that should show
				if (!city.shouldShow) continue

				// Get a new label from the pool
				pooled = this.getPooledLabel(city.importance)
				if (!pooled) {
					// Pool exhausted - skip this city
					continue
				}

				pooled.cityId = city.id
				pooled.label.cityId = city.id
				this.activeLabels.set(city.id, pooled)

				// Update label text and weather
				pooled.label.text = city.name
				pooled.label.weather = city.weather
				pooled.animState.weather = city.weather

				// Initialize animation state for fade in
				pooled.animState.currentAlpha = 0
				pooled.animState.targetAlpha = city.alpha
				pooled.animState.stateChangeTime = now
				pooled.animState.lastUpdateTime = now
			} else {
				// Existing label - update target alpha based on shouldShow
				if (city.shouldShow) {
					// A label that never became visible (still in its show
					// hysteresis window when it was deselected) restarts the
					// show clock, so it must stay selected for a full
					// hysteresis period before appearing. A partially faded
					// label resumes fading back in immediately - it is
					// already on screen, so recovery must not stall.
					if (pooled.animState.targetAlpha === 0 && pooled.animState.currentAlpha === 0) {
						pooled.animState.stateChangeTime = now
					}
					pooled.animState.targetAlpha = city.alpha
				} else {
					// Fading out - just update position, keep fading
					if (pooled.animState.targetAlpha > 0) {
						pooled.animState.targetAlpha = 0
						pooled.animState.stateChangeTime = now
					}
					// Geometric hide: start dissolving now, not after the gate
					if (city.alpha <= 0) {
						pooled.animState.stateChangeTime = 0
					}
				}
				pooled.animState.lastUpdateTime = now

				// Update weather data
				pooled.label.weather = city.weather
				pooled.animState.weather = city.weather
			}

			// Always update screen position and depth (even for fading labels)
			pooled.animState.screenX = city.screenX
			pooled.animState.screenY = city.screenY
			pooled.animState.cityScreenX = city.cityScreenX
			pooled.animState.cityScreenY = city.cityScreenY
			pooled.animState.worldDepth = city.worldDepth
			pooled.animState.importance = city.importance
		}

		if (now - this.lastDedupeTime >= TEMP_DEDUPE_INTERVAL_MS) {
			this.lastDedupeTime = now
			this.dedupeSmallTierTemps()
		}
	}

	/**
	 * Hide the bare temperature on small-tier labels that sit near a
	 * pill-bearing label showing (as displayed) nearly the same value.
	 * References are labels already on screen (past their show gate and
	 * mostly faded in), so the filter rides on the existing hysteresis and
	 * a town's number doesn't flicker with every placement pass.
	 */
	private dedupeSmallTierTemps(): void {
		const references: { x: number; y: number; temp: number }[] = []
		for (const pooled of this.activeLabels.values()) {
			const anim = pooled.animState
			const tempC = anim.weather?.temp_c
			if (tempC == null || anim.targetAlpha <= 0 || anim.currentAlpha < 0.5) continue
			if (!TIER_WEATHER_STYLE[anim.importance].chip) continue
			references.push({ x: anim.screenX, y: anim.screenY, temp: displayTemp(tempC) })
		}

		const radiusSq = TEMP_DEDUPE_RADIUS_PX * TEMP_DEDUPE_RADIUS_PX
		for (const pooled of this.activeLabels.values()) {
			const anim = pooled.animState
			if (!TIER_WEATHER_STYLE[anim.importance].tempText) continue
			const tempC = anim.weather?.temp_c
			let suppressed = false
			if (tempC != null) {
				const temp = displayTemp(tempC)
				for (const ref of references) {
					const dx = ref.x - anim.screenX
					const dy = ref.y - anim.screenY
					if (dx * dx + dy * dy <= radiusSq && Math.abs(ref.temp - temp) < TEMP_DEDUPE_MIN_DIFF_DEG) {
						suppressed = true
						break
					}
				}
			}
			pooled.label.tempSuppressed = suppressed
		}
	}

	/**
	 * Override render to animate label alphas each frame
	 */
	override render(state: RenderState): RenderActionSet[] {
		const now = performance.now()
		const deltaTime = this.lastFrameTime > 0 ? now - this.lastFrameTime : 16
		this.lastFrameTime = now

		// Animate all active labels
		// Track labels to remove after iteration
		this.labelsToRemove.length = 0

		// Cause-aware timing: long gates while panning (suppress contention
		// jitter), short gates while zooming (changes are legitimate and the
		// label set must track altitude promptly)
		const showDelay = this.motion === 'zooming' ? HYSTERESIS_SHOW_ZOOM_MS
			: this.motion === 'panning' ? HYSTERESIS_SHOW_PAN_MS
			: HYSTERESIS_SHOW_MS
		const hideDelay = this.motion === 'zooming' ? HYSTERESIS_HIDE_ZOOM_MS
			: this.motion === 'panning' ? HYSTERESIS_HIDE_PAN_MS
			: HYSTERESIS_HIDE_MS
		const fadeInDuration = this.motion === 'zooming' ? FADE_IN_ZOOM_MS : FADE_IN_DURATION_MS
		const fadeOutDuration = this.motion === 'zooming' ? FADE_OUT_ZOOM_MS : FADE_OUT_DURATION_MS

		// Footprints of the labels drawn this frame, shared so division names
		// and road shields keep clear of them
		const occupied: LabelRect[] = []

		for (const [cityId, pooled] of this.activeLabels) {
			const anim = pooled.animState
			const prevAlpha = anim.currentAlpha

			// Calculate time since state change for hysteresis
			const timeSinceChange = now - anim.stateChangeTime

			if (anim.targetAlpha > anim.currentAlpha) {
				// Fading IN - smooth animation after the show delay
				if (timeSinceChange >= showDelay) {
					const fadeSpeed = deltaTime / fadeInDuration
					anim.currentAlpha = Math.min(anim.targetAlpha, anim.currentAlpha + fadeSpeed)
				}
			} else if (anim.targetAlpha < anim.currentAlpha) {
				// Fading OUT - smooth dissolve after the hide delay.
				// A brief loser that wins the next placement pass recovers from
				// its partial fade instead of having popped off screen.
				if (timeSinceChange >= hideDelay) {
					const fadeSpeed = deltaTime / fadeOutDuration
					anim.currentAlpha = Math.max(anim.targetAlpha, anim.currentAlpha - fadeSpeed)
				}
			}

			// Update label visibility and position
			if (anim.currentAlpha > 0.01) {
				pooled.label.visible = true

				// Update position (has internal early-out if unchanged)
				const labelWidth = pooled.label.width
				const labelHeight = pooled.label.height
				const labelX = anim.screenX - labelWidth / 2
				const labelY = anim.screenY - labelHeight - 4
				pooled.label.offset(labelX, labelY)
				occupied.push({ left: labelX, top: labelY, right: labelX + labelWidth, bottom: labelY + labelHeight })

				// Set perspective depth for world-pass occlusion
				pooled.label.worldDepth = anim.worldDepth

				// Calculate city dot position relative to label top-left
				const cityDotRelX = anim.cityScreenX - labelX
				const cityDotRelY = anim.cityScreenY - labelY
				pooled.label.setCityDotOffset(cityDotRelX, cityDotRelY)

				// Only update color if alpha changed significantly (>2%)
				// The color setter also has its own threshold check
				if (Math.abs(anim.currentAlpha - prevAlpha) > 0.02) {
					const baseColor = CITY_LABEL_COLORS[anim.importance]
					pooled.label.color = rgba(baseColor.r, baseColor.g, baseColor.b, baseColor.a * anim.currentAlpha)
				}
			} else {
				pooled.label.visible = false

				// If fully faded out and target is 0, return to pool
				if (anim.targetAlpha === 0) {
					this.labelsToRemove.push(cityId)
				}
			}
		}

		labelOccupancy.publish(OCCUPANCY_OWNER, occupied)

		// Remove fully faded labels from active set
		for (const cityId of this.labelsToRemove) {
			const pooled = this.activeLabels.get(cityId)
			if (pooled) {
				pooled.cityId = null
				pooled.animState.currentAlpha = 0
				pooled.animState.targetAlpha = 0
				pooled.label.tempSuppressed = false
				this.activeLabels.delete(cityId)

				// Dismiss a hover callout left dangling by a label that faded
				// out from under the cursor (hidePreview no-ops otherwise)
				const cityNode = this.cityLayer.getCityNodeById(cityId)
				if (cityNode) {
					(this.context as EarthBrowserContext).hoveredObjectCallout?.hidePreview(cityNode)
				}
			}
		}

		return super.render(state)
	}

	/**
	 * Clear all labels
	 */
	public clearLabels(): void {
		for (const pooled of this.labelPool) {
			pooled.label.visible = false
			pooled.cityId = null
			pooled.animState.currentAlpha = 0
			pooled.animState.targetAlpha = 0
		}
		this.activeLabels.clear()
		labelOccupancy.clear(OCCUPANCY_OWNER)
	}

	/**
	 * Get statistics about label usage
	 */
	public getStats(): { total: number; active: number; poolSize: number } {
		return {
			total: this.labelPool.length,
			active: this.activeLabels.size,
			poolSize: this.labelPool.filter(p => p.cityId === null).length
		}
	}

	override destroy(): void {
		if (typeof document !== 'undefined' && document.fonts) {
			document.fonts.removeEventListener('loadingdone', this.fontsLoadedHandler)
		}
		this.clearLabels()
		super.destroy()
	}
}
